import React, { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { UGC_MENU, UgcFeatureKey, findUgcMenuEntry, isUgcMenuGroup } from '@/common/app/ugcMenu'

// カード共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'

/**
 * 設定ガイド（2026-09-29 作り直し）。
 *
 * **いまメニューにある機能だけを案内する。** 各手順は `feature`（common/app/ugcMenu.ts のキー）を持ち、
 * メニュー定義にその機能が無い手順は表示しない。準備中の手順もここに書いておけば、
 * 画面ができてメニューに追加された時点で自動的にガイドに現れる。
 * 以前は準備中の機能・構想段階の図まで載せていたため、何が使えるのか分からなかった。
 */

interface GuideStep {
  label: string
  desc: string
  feature: UgcFeatureKey
  icon: string
}

interface Scenario {
  id: string
  title: string
  icon: string
  color: string
  time: string
  description: string
  steps: GuideStep[]
  flow?: string
}

const SCENARIOS: Scenario[] = [
  {
    id: 'setup',
    title: '初期セットアップ',
    icon: '🚀',
    color: '#6366f1',
    time: '約30分',
    description: '使い始めるための設定です。上から順に進めてください',
    steps: [
      { label: 'ブランド情報', desc: 'ブランド名・ロゴ・許諾メッセージ。レビュー依頼メールや投稿フォームに表示されます', feature: 'instagramSettings', icon: '🏢' },
      { label: 'Instagram接続', desc: 'Instagram ビジネスアカウントのトークンを接続し、収集するハッシュタグと頻度を決めます', feature: 'instagramSettings', icon: '📸' },
      { label: 'レビュー依頼メールと投稿フォーム', desc: '差出人・文面・送るタイミング、投稿フォームのデザインとカスタム項目（肌タイプ・サイズ感など）', feature: 'mail', icon: '✉️' },
      { label: 'サイトに出す枠を作る', desc: '枠ごとに、出す内容（レビュー／写真投稿／混在）・レイアウト・並び順・件数を決めます', feature: 'displayRules', icon: '🧩' },
      { label: 'ECサイトにタグを貼る', desc: '基本タグを全ページに、表示用のタグを商品ページやトップページに貼ります', feature: 'tagEmbed', icon: '🏷️' }
    ]
  },
  {
    id: 'collect',
    title: 'レビューを集める',
    icon: '📥',
    color: '#e67e22',
    time: '自動運用',
    description: '購入者にレビュー依頼メールを送り、Instagram の投稿を集めます',
    steps: [
      { label: '送るタイミングと文面', desc: 'ブランド・カテゴリ・初回購入などの条件ごとに、出荷後○日で送るルールと文面を決めます', feature: 'mail', icon: '📅' },
      { label: '送信を始める', desc: '送信モードを「テスト」で確認してから「本番」にします。出荷完了から自動で依頼が作られます', feature: 'mail', icon: '⚡' },
      { label: 'フォローアップ', desc: '未投稿の方に、開封・クリックの状況に応じて再送します', feature: 'mail', icon: '🔄' },
      { label: 'Instagram の投稿を集める', desc: 'ハッシュタグ・タグ付けされた投稿を収集し、サイトで紹介する許諾を依頼します', feature: 'instagram', icon: '📸' },
      { label: '他サービスのレビューを取り込む', desc: '楽天市場などのレビューをCSVで取り込みます', feature: 'reviews', icon: '📦' }
    ],
    flow: '出荷完了 → ○日後にメール送信 → 投稿フォームで投稿 → レビュー一覧に登録'
  },
  {
    id: 'manage',
    title: 'レビューを管理する',
    icon: '✅',
    color: '#27ae60',
    time: '日常運用',
    description: '集まったレビューを確認して公開し、返信や社内メモで管理します',
    steps: [
      { label: 'AIで採点する', desc: 'レビューや投稿の質をAIが100点満点で採点。表示順や自動振り分けに使われます', feature: 'aiScore', icon: '🤖' },
      { label: '確認して公開する', desc: '未確認のレビューを確認し、公開／非公開／保留を決めます', feature: 'reviews', icon: '📋' },
      { label: 'ショップから返信する', desc: 'レビューにショップの返信コメントを付けます（サイトに表示されます）', feature: 'reviews', icon: '💬' },
      { label: '社内タグ・メモ', desc: '商品開発・物流などの社内タグとメモを付けます（お客様には表示されません）', feature: 'reviews', icon: '🏷️' },
      { label: '写真に商品を紐づける', desc: '写真の上に商品を紐づけ、写真から商品ページへ誘導します', feature: 'productTagging', icon: '📌' },
      { label: '削除のご要望に対応する', desc: 'お客様から依頼があれば、メールアドレスかお名前でデータを探して匿名化します', feature: 'compliance', icon: '🗑️' }
    ],
    flow: 'レビュー投稿 → AI採点 → 確認・公開 → サイトに表示'
  },
  {
    id: 'display',
    title: 'ECサイトに表示する',
    icon: '🖥️',
    color: '#3498db',
    time: '設定後は自動',
    description: '公開したレビュー・写真投稿を、ECサイトの商品ページやトップページに表示します',
    steps: [
      { label: '表示枠を作る', desc: 'レイアウト（格子／横スクロール／縦並び）・並び順・件数・見た目を枠ごとに決めます', feature: 'displayRules', icon: '🎨' },
      { label: 'タグを貼る', desc: '商品ページにはその商品の投稿を、トップページにはサイト全体の人気の投稿を出せます', feature: 'tagEmbed', icon: '🏷️' },
      { label: 'Google検索に★を出す', desc: 'レビューの構造化データで、検索結果に★とレビュー数を表示します', feature: 'seo', icon: '🔍' },
      // 準備中（メニューに追加されると自動で表示される）
      { label: 'A/Bテスト', desc: '表示枠の見せ方を比べ、購入率の高い組み合わせを見つけます', feature: 'abTest', icon: '🔬' }
    ],
    flow: 'ECサイト訪問 → 表示枠に公開中のUGCを表示 → 閲覧・クリック・購入を計測'
  },
  {
    id: 'analyze',
    title: '効果を測定する',
    icon: '📊',
    color: '#9b59b6',
    time: '週次確認推奨',
    description: 'UGCの表示が購入につながっているか、お客様が何を感じているかを確認します',
    steps: [
      { label: 'ダッシュボード', desc: '依頼から購入までの流れと、いまやるべきことを確認します', feature: 'dashboard', icon: '🧭' },
      { label: 'CVRダッシュボード', desc: 'UGCを見た人と見ていない人の購入率を比べます。日別の推移・流入経路別の成果も見られます', feature: 'cvr', icon: '📈' },
      { label: 'VOC/NPS分析', desc: 'レビューの頻出キーワードと満足度（NPS）を確認し、LP向けコピーをAIで作ります', feature: 'voc', icon: '💬' },
      { label: 'コンテンツ鮮度アラート', desc: '見飽きられてクリック率が落ちた投稿を見つけ、非公開や入れ替えを行います', feature: 'wear', icon: '⚠️' }
    ],
    flow: 'UGC表示 → クリック・購入を計測 → 効果を確認 → 入れ替え・改善 → 再計測'
  },
  {
    // 現時点ではすべて準備中。画面ができてメニューに追加されると、このシナリオが表示される
    id: 'grow',
    title: '顧客を育てる',
    icon: '🌱',
    color: '#1abc9c',
    time: '中長期施策',
    description: 'レビュー投稿を習慣にし、ファンを増やします',
    steps: [
      { label: 'ロイヤルティ', desc: 'レビュー投稿でポイントを付与し、ランクで特典を出します', feature: 'loyalty', icon: '🏆' },
      { label: 'Q&A', desc: '商品ページのQ&Aで購入前の不安を解消します', feature: 'qa', icon: '❓' },
      { label: 'スタッフ投稿', desc: '店舗スタッフによる商品紹介を集めます', feature: 'staff', icon: '👤' },
      { label: 'ギフティング', desc: '商品を送って投稿を依頼します', feature: 'gifting', icon: '🎁' },
      { label: 'OMO（店舗連携）', desc: '店舗のQRコードからレビューを集めます', feature: 'omo', icon: '🏪' },
      { label: '動画コマース', desc: '動画と商品を紐づけて配信・計測します', feature: 'video', icon: '🎬' },
      { label: 'ポータル配信', desc: '許諾済みのUGCを消費者向けポータルに配信します', feature: 'portal', icon: '🌐' }
    ]
  }
]

export default function UGCGuide() {
  const { siteId } = useParams()
  const sid = Number(siteId)
  const [openId, setOpenId] = useState<string | null>('setup')

  // メニューにある機能の手順だけを残し、手順が1つも無いシナリオは出さない
  const scenarios = SCENARIOS.map((s) => ({
    ...s,
    steps: s.steps.flatMap((step) => {
      const entry = findUgcMenuEntry(step.feature)
      return entry ? [{ ...step, path: entry.path(sid), menuName: entry.name }] : []
    })
  })).filter((s) => s.steps.length > 0)

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='📖 設定ガイド'
        description='いまお使いいただける機能を、作業の順番に沿ってご案内します。各手順を押すと、その設定画面に移動します。'
      />

      {/* 全体フロー */}
      <div className={`${CARD} mb-6 p-5`}>
        <h3 className='text-primary mb-4 text-center text-[15px] font-bold'>UGC活用の流れ</h3>
        <div className='flex flex-wrap items-center justify-center gap-2'>
          {scenarios.map((s, i) => (
            <React.Fragment key={s.id}>
              <div
                onClick={() => setOpenId(openId === s.id ? null : s.id)}
                className={`min-w-[120px] cursor-pointer rounded-xl border-2 px-4 py-3 text-center transition-all ${openId === s.id ? 'text-on-bg-fill' : ''}`}
                style={{
                  background: openId === s.id ? s.color : `${s.color}15`,
                  color: openId === s.id ? undefined : s.color,
                  borderColor: s.color
                }}
              >
                <div className='mb-1 text-2xl'>{s.icon}</div>
                <div className='text-xs font-bold'>{s.title}</div>
                <div className='text-[9px] opacity-70'>{s.time}</div>
              </div>
              {i < scenarios.length - 1 && <span className='text-tertiary text-lg'>→</span>}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* シナリオ詳細 */}
      <div className='flex flex-col gap-3'>
        {scenarios.map((s) => {
          const isOpen = openId === s.id
          return (
            <div key={s.id} className={`${CARD} overflow-hidden p-0`} style={{ borderLeft: `4px solid ${s.color}` }}>
              <div
                onClick={() => setOpenId(isOpen ? null : s.id)}
                className='flex cursor-pointer items-center gap-3 px-5 py-4'
                style={{ background: isOpen ? `${s.color}08` : undefined }}
              >
                <span className='text-[28px]'>{s.icon}</span>
                <div className='flex-1'>
                  <div className='text-primary text-base font-bold'>{s.title}</div>
                  <div className='text-secondary mt-0.5 text-xs'>{s.description}</div>
                </div>
                <span
                  className='rounded-2xl px-3 py-1 text-[10px] font-semibold'
                  style={{ background: `${s.color}15`, color: s.color }}
                >
                  {s.time}
                </span>
                <span className='text-tertiary text-sm transition-transform' style={{ transform: isOpen ? 'rotate(180deg)' : '' }}>
                  ▼
                </span>
              </div>

              {isOpen && (
                <div className='px-5 pb-5'>
                  {s.flow && (
                    <div className='mb-4 rounded-lg p-3 text-center' style={{ background: `${s.color}08` }}>
                      <div className='text-secondary mb-1 text-[10px]'>流れ</div>
                      <div className='text-[13px] font-semibold' style={{ color: s.color }}>
                        {s.flow}
                      </div>
                    </div>
                  )}
                  <div className='flex flex-col gap-2'>
                    {s.steps.map((step, i) => (
                      <Link key={i} to={step.path} className='no-underline'>
                        <div
                          className='border-secondary bg-surface-secondary flex cursor-pointer items-center gap-3 rounded-[10px] border px-3.5 py-3 transition-all'
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = `${s.color}08`
                            e.currentTarget.style.borderColor = s.color
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = ''
                            e.currentTarget.style.borderColor = ''
                          }}
                        >
                          <div
                            className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-base'
                            style={{ background: `${s.color}15` }}
                          >
                            {step.icon}
                          </div>
                          <div className='bg-fill-brand text-on-bg-fill flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold'>
                            {i + 1}
                          </div>
                          <div className='flex-1'>
                            <div className='text-primary text-[13px] font-semibold'>{step.label}</div>
                            <div className='text-secondary mt-0.5 text-[11px]'>{step.desc}</div>
                          </div>
                          <span className='text-[11px] whitespace-nowrap' style={{ color: s.color }}>
                            {step.menuName} →
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* メニュー一覧（メニュー定義から作るので、メニューが増えれば自動で増える） */}
      <div className={`${CARD} mt-6 p-6`}>
        <h3 className='text-primary m-0 text-[15px] font-bold'>メニューの一覧</h3>
        <p className='text-secondary mt-1 mb-0 text-xs'>左のメニューの各画面でできることです。</p>
        <div className='mt-4 flex flex-col gap-4'>
          {UGC_MENU.filter((n) => isUgcMenuGroup(n) || n.key !== 'guide').map((node) => {
            const entries = isUgcMenuGroup(node) ? node.items : [node]
            return (
              <div key={isUgcMenuGroup(node) ? node.name : node.key}>
                {isUgcMenuGroup(node) && <div className='text-secondary mb-1 text-[11px] font-semibold'>{node.name}</div>}
                <div className='flex flex-col gap-1'>
                  {entries.map((e) => (
                    <Link key={e.key} to={e.path(sid)} className='flex flex-wrap gap-x-3 no-underline'>
                      <span className='text-link text-[13px] font-semibold'>{e.name}</span>
                      <span className='text-secondary text-[12px]'>{e.desc}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </Main>
  )
}
